/**
 * PATH: packages/quote-builder/src/grid/a1.ts
 * FILE_ID: QB-GRID-A1-001
 * PURPOSE: Pure A1-notation helpers — column letter ↔ index, cell ref
 *   parsing and range expansion — for free-form spreadsheet surfaces.
 * WHY: ARC-3 (U06). Moved out of the deleted kanban workbook fork so the
 *   ONE shared grid package owns A1 addressing for every consumer.
 * FLOW: "B3" ↔ {col:1,row:2} ; "A1:B2" → ["A1","B1","A2","B2"]
 */

const LETTERS_RE = /^[A-Z]+$/

/** "A" → 0, "B" → 1, "Z" → 25, "AA" → 26. Returns -1 for invalid input. */
export function colToIndex(letters: string): number {
  const up = letters.toUpperCase()
  if (!LETTERS_RE.test(up)) return -1
  let n = 0
  for (let i = 0; i < up.length; i++) {
    n = n * 26 + (up.charCodeAt(i) - 64)
  }
  return n - 1
}

/** 0 → "A", 25 → "Z", 26 → "AA". */
export function indexToCol(index: number): string {
  let n = index
  let s = ''
  while (n >= 0) {
    s = String.fromCharCode((n % 26) + 65) + s
    n = Math.floor(n / 26) - 1
  }
  return s
}

/** "B3" → {col:1,row:2} (0-based). Null if not a valid ref. */
export function parseRef(ref: string): { col: number; row: number } | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref.trim())
  if (!m) return null
  const col = colToIndex(m[1])
  const row = parseInt(m[2], 10) - 1
  if (col < 0 || row < 0) return null
  return { col, row }
}

/** Expand "A1":"B2" → ["A1","B1","A2","B2"] (row-major, normalised). */
export function expandRange(a: string, b: string): string[] {
  const pa = parseRef(a)
  const pb = parseRef(b)
  if (!pa || !pb) return []
  const c0 = Math.min(pa.col, pb.col)
  const c1 = Math.max(pa.col, pb.col)
  const r0 = Math.min(pa.row, pb.row)
  const r1 = Math.max(pa.row, pb.row)
  const keys: string[] = []
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      keys.push(`${indexToCol(c)}${r + 1}`)
    }
  }
  return keys
}
